import { Component, ComponentRef, inject, OnDestroy, OnInit, Type, ViewChild, ViewContainerRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  IonHeader,
  IonToolbar,
  IonText,
  IonButtons,
  IonButton,
  IonIcon,
  IonSegment,
  IonSegmentButton,
  IonContent,
  IonFooter,
  ModalController,
} from '@ionic/angular';
import { LetDirective } from '@ngrx/component';
import { addIcons } from 'ionicons';
import { closeOutline } from 'ionicons/icons';
import { Subscription } from 'rxjs';

import { CreateAssessmentComponent } from '@ninety/mobile/pages-shared/assessments-shared';
import { CreateDiscussionComponent } from '@ninety/mobile/pages-shared/discussions-shared';
import { CreateHeadlineComponent } from '@ninety/mobile/pages-shared/headlines-shared';
import { CreateIssueComponent } from '@ninety/mobile/pages-shared/issues-shared';
import { CreateRockComponent } from '@ninety/mobile/pages-shared/shared-rocks';
import { CreateToDoComponent } from '@ninety/mobile/pages-shared/to-dos-shared';
import { LinkedItem, NtyUniversalCreateComponent, NtyUniversalCreateFeature } from '@ninety/mobile/platform/api';
import { LoadingComponent } from '@ninety/mobile/platform/components';

import { UniversalCreateStore } from '../state/universal-create.store';

import { UniversalCreateButtonTextComponent } from './universal-create-button-text.component';

/** Maps features to the Component responsible for creating items for the given feature */
const NinetyUniversalComponentMap: Record<NtyUniversalCreateFeature, Type<NtyUniversalCreateComponent>> = {
  todo: CreateToDoComponent,
  issue: CreateIssueComponent,
  rock: CreateRockComponent,
  discussion: CreateDiscussionComponent,
  assessment: CreateAssessmentComponent,
  headline: CreateHeadlineComponent,
};

@Component({
  selector: 'nty-universal-create',
  imports: [
    FormsModule,
    LetDirective,
    UniversalCreateButtonTextComponent,
    LoadingComponent,
    IonHeader,
    IonToolbar,
    IonText,
    IonButtons,
    IonButton,
    IonIcon,
    IonSegment,
    IonSegmentButton,
    IonContent,
    IonFooter,
  ],
  providers: [UniversalCreateStore],
  templateUrl: './universal-create.component.html',
  styleUrls: ['./universal-create.component.scss'],
})
export class UniversalCreateComponent implements OnInit, OnDestroy {
  readonly store = inject(UniversalCreateStore);
  readonly modalCtrl = inject(ModalController);

  private componentRef?: ComponentRef<NtyUniversalCreateComponent>;
  private _subscriptions?: Subscription;

  /** View container where dynamic form components are appended to */
  @ViewChild('content', { read: ViewContainerRef, static: true })
  container!: ViewContainerRef;

  /** The currently selected feature the user is creating an item for */
  selectedFeature: NtyUniversalCreateFeature = 'issue';

  constructor() {
    addIcons({ closeOutline });
  }

  ngOnInit(): void {
    this.createComponent();
    this.subscribe();
  }

  ngOnDestroy(): void {
    this.unsubscribe();
  }

  onDismiss() {
    this.store.resetCreateFormState();
    this.modalCtrl.dismiss();
  }

  onSegmentChange() {
    this.createComponent();
  }

  onSave(linkedItem?: LinkedItem) {
    this.componentRef?.instance.create(linkedItem);
  }

  private createComponent() {
    this.container?.clear();
    this.componentRef = this.container?.createComponent(NinetyUniversalComponentMap[this.selectedFeature]);
  }

  private subscribe(): void {
    this.unsubscribe();
    this.add(
      this.store.defaultTab$.subscribe(tab => {
        this.selectedFeature = tab;
        this.onSegmentChange();
      })
    );
  }

  private unsubscribe(): void {
    this._subscriptions?.unsubscribe();
    this._subscriptions = undefined;
  }

  private add(subscription: Subscription): void {
    this._subscriptions ??= new Subscription();
    this._subscriptions.add(subscription);
  }
}
